import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
  inject,
  NgZone,
  HostListener
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { GoogleMapsModule, GoogleMap } from '@angular/google-maps';
import { FlightMapService } from '../../../core/services/flight-map.service';
import { FieldService } from '../../../core/data-services/field.service';
import { FieldFlightPath } from '../../../core/response-models/field-flight-path.model';
import { extractCoordinatesFromKml } from '../../utils/kml.utils';
import {
  coordsToGeoJsonPolygon,
  geoJsonPolygonToCoords,
  extractPolygonFromKml
} from '../../../core/utils/geojson-utils';
import {
  computeConvexHull,
  applyBuffer,
  computeCentroid,
  isHighVertexCount,
  getVertexCount
} from '../../../core/utils/boundary-utils';

/** Coordinate interface */
interface Coordinate {
  lat: number;
  lng: number;
}

/**
 * BoundaryEditorComponent provides an interactive Google Map for drawing,
 * importing, and editing field boundary polygons.
 *
 * Supports polygon drawing via Google Maps Drawing Manager, file import
 * (KML/GeoJSON), and import from flight path data.
 *
 * On mobile viewports (below Bootstrap md breakpoint, 768px), renders in
 * read-only mode without editing tools.
 *
 * Reference: /agent-os/specs/2026-02-27-field-boundary-editor/spec.md
 */
@Component({
  selector: 'app-boundary-editor',
  standalone: true,
  imports: [CommonModule, FormsModule, GoogleMapsModule],
  templateUrl: './boundary-editor.component.html',
  styleUrls: ['./boundary-editor.component.scss']
})
export class BoundaryEditorComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild(GoogleMap) googleMap!: GoogleMap;

  private flightMapService = inject(FlightMapService);
  private fieldService = inject(FieldService);
  private ngZone = inject(NgZone);

  /** Existing boundary GeoJSON to load in edit mode */
  @Input() boundaryGeojson: any | null = null;

  /** Field center latitude */
  @Input() latitude: number | null = null;

  /** Field center longitude */
  @Input() longitude: number | null = null;

  /** Field ID for flight path query */
  @Input() fieldId: string | null = null;

  /** Emits GeoJSON Polygon when polygon is drawn/modified, or null when cleared */
  @Output() boundaryChanged = new EventEmitter<any>();

  /** Emits calculated acreage value */
  @Output() acreageChanged = new EventEmitter<number>();

  /** Emits original file content when boundary is imported from KML */
  @Output() originalFileChanged = new EventEmitter<string | null>();

  /** Default zoom for US overview */
  zoom = 6;

  /** Map center coordinates */
  center: google.maps.LatLngLiteral = { lat: 39.8283, lng: -98.5795 };

  /** Google Maps configuration */
  mapOptions: google.maps.MapOptions = {
    mapTypeId: 'satellite',
    zoomControl: true,
    scrollwheel: true,
    disableDoubleClickZoom: false,
    maxZoom: 20,
    minZoom: 2,
    streetViewControl: false
  };

  /** Current polygon on the map */
  currentPolygon: google.maps.Polygon | null = null;

  /** Cached map instance (set from mapInitialized event, avoids @ViewChild timing issues) */
  private mapInstance: google.maps.Map | null = null;

  /** Drawing Manager instance */
  private drawingManager: google.maps.drawing.DrawingManager | null = null;

  /** Flag indicating if the drawing library is loaded */
  drawingLibraryLoaded = false;

  /** Mobile read-only mode detection */
  isMobile = false;

  /** File drag state */
  isDragging = false;

  /** File processing state */
  isProcessing = false;

  /** Upload error message */
  uploadError: string | null = null;

  /** High vertex count warning */
  vertexWarning: string | null = null;

  /** Whether a polygon currently exists on the map */
  hasPolygon = false;

  /** Current polygon coordinates for display */
  polygonCoordinates: Coordinate[] = [];

  /** Whether coordinate details are expanded */
  showCoordinates = false;

  /** Flight path options for import dropdown */
  flightPaths: FieldFlightPath[] = [];

  /** Loading state for flight paths */
  loadingFlightPaths = false;

  /** Selected flight path ID */
  selectedFlightPathId: string | null = null;

  /** Listeners to clean up on destroy */
  private polygonListeners: google.maps.MapsEventListener[] = [];

  ngOnInit(): void {
    this.checkMobileViewport();
    this.updateMapCenter();
    this.loadDrawingLibrary();

    if (this.fieldId) {
      this.loadFlightPaths();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['latitude'] || changes['longitude'] || changes['boundaryGeojson']) {
      this.updateMapCenter();
    }

    if (changes['boundaryGeojson'] && this.boundaryGeojson && !this.hasPolygon) {
      this.loadExistingBoundary();
    }

    if (changes['fieldId'] && this.fieldId) {
      this.loadFlightPaths();
    }
  }

  ngOnDestroy(): void {
    this.cleanupPolygon();
    if (this.drawingManager) {
      this.drawingManager.setMap(null);
      this.drawingManager = null;
    }
  }

  @HostListener('window:resize')
  onResize(): void {
    this.checkMobileViewport();
  }

  /**
   * Get the Google Map instance, preferring the cached instance from mapInitialized
   */
  private getMap(): google.maps.Map | null {
    return this.mapInstance || this.googleMap?.googleMap || null;
  }

  /**
   * Detect viewport below Bootstrap md breakpoint (768px)
   */
  private checkMobileViewport(): void {
    this.isMobile = window.innerWidth < 768;
  }

  /**
   * Update map center based on inputs
   */
  private updateMapCenter(): void {
    if (this.latitude != null && this.longitude != null) {
      this.center = { lat: this.latitude, lng: this.longitude };
      this.zoom = 15;
    } else if (this.boundaryGeojson) {
      // Compute centroid from boundary when lat/lon are null
      const coords = geoJsonPolygonToCoords(this.boundaryGeojson);
      if (coords.length > 0) {
        const centroid = computeCentroid(coords);
        this.center = centroid;
        this.zoom = 15;
      }
    } else {
      this.center = { lat: 39.8283, lng: -98.5795 };
      this.zoom = 6;
    }
  }

  /**
   * Load the Google Maps Drawing library and initialize Drawing Manager
   */
  private async loadDrawingLibrary(): Promise<void> {
    if (this.isMobile) {
      return;
    }

    try {
      await google.maps.importLibrary('drawing');
      this.drawingLibraryLoaded = true;
    } catch (error) {
      console.error('Failed to load Google Maps drawing library:', error);
    }
  }

  /**
   * Called when the Google Map component is initialized.
   * Accepts the map instance directly from (mapInitialized) event
   * to avoid @ViewChild timing issues inside @if blocks.
   */
  onMapReady(map?: google.maps.Map): void {
    const googleMap = map || this.googleMap?.googleMap;
    if (!googleMap) {
      return;
    }

    this.mapInstance = googleMap;

    // Trigger resize after initialization
    setTimeout(() => {
      google.maps.event.trigger(googleMap, 'resize');

      // Load existing boundary if present
      if (this.boundaryGeojson && !this.hasPolygon) {
        this.loadExistingBoundary();
      }

      // Initialize Drawing Manager for desktop only
      if (!this.isMobile && this.drawingLibraryLoaded) {
        this.initDrawingManager(googleMap);
      } else if (!this.isMobile) {
        // Wait for drawing library to load then init
        this.waitForDrawingLibrary(googleMap);
      }
    }, 100);
  }

  /**
   * Wait for the drawing library to finish loading, then initialize
   */
  private async waitForDrawingLibrary(map: google.maps.Map): Promise<void> {
    try {
      await google.maps.importLibrary('drawing');
      this.drawingLibraryLoaded = true;
      this.initDrawingManager(map);
    } catch (error) {
      console.error('Failed to load drawing library:', error);
    }
  }

  /**
   * Initialize the Drawing Manager with polygon drawing mode only
   */
  private initDrawingManager(map: google.maps.Map): void {
    if (this.drawingManager) {
      return;
    }

    this.drawingManager = new google.maps.drawing.DrawingManager({
      drawingMode: this.hasPolygon ? null : google.maps.drawing.OverlayType.POLYGON,
      drawingControl: true,
      drawingControlOptions: {
        position: google.maps.ControlPosition.TOP_CENTER,
        drawingModes: [google.maps.drawing.OverlayType.POLYGON]
      },
      polygonOptions: {
        fillColor: '#FF3B30',
        fillOpacity: 0.2,
        strokeColor: '#FF3B30',
        strokeOpacity: 0.9,
        strokeWeight: 2,
        editable: true,
        draggable: false
      }
    });

    this.drawingManager.setMap(map);

    // Listen for polygon completion
    google.maps.event.addListener(this.drawingManager, 'polygoncomplete', (polygon: google.maps.Polygon) => {
      this.ngZone.run(() => {
        // Single polygon enforcement: remove existing polygon first
        this.cleanupPolygon();

        this.currentPolygon = polygon;
        this.hasPolygon = true;

        // Switch to null drawing mode to prevent drawing another polygon
        this.drawingManager?.setDrawingMode(null);

        // Attach edit listeners
        this.attachPolygonListeners(polygon);

        // Calculate acreage and emit boundary
        this.onPolygonChanged();
      });
    });
  }

  /**
   * Attach listeners to a polygon for vertex editing and path changes
   */
  private attachPolygonListeners(polygon: google.maps.Polygon): void {
    this.cleanupPolygonListeners();

    const path = polygon.getPath();

    // Listen for vertex changes
    this.polygonListeners.push(
      google.maps.event.addListener(path, 'set_at', () => {
        this.ngZone.run(() => this.onPolygonChanged());
      })
    );

    this.polygonListeners.push(
      google.maps.event.addListener(path, 'insert_at', () => {
        this.ngZone.run(() => this.onPolygonChanged());
      })
    );

    this.polygonListeners.push(
      google.maps.event.addListener(path, 'remove_at', () => {
        this.ngZone.run(() => this.onPolygonChanged());
      })
    );

    // Listen for drag end
    this.polygonListeners.push(
      google.maps.event.addListener(polygon, 'dragend', () => {
        this.ngZone.run(() => this.onPolygonChanged());
      })
    );
  }

  /**
   * Clean up polygon event listeners
   */
  private cleanupPolygonListeners(): void {
    for (const listener of this.polygonListeners) {
      google.maps.event.removeListener(listener);
    }
    this.polygonListeners = [];
  }

  /**
   * Remove the current polygon from the map
   */
  private cleanupPolygon(): void {
    this.cleanupPolygonListeners();
    if (this.currentPolygon) {
      this.currentPolygon.setMap(null);
      this.currentPolygon = null;
    }
    this.hasPolygon = false;
    this.polygonCoordinates = [];
  }

  /**
   * Called when the polygon is drawn, edited, or imported.
   * Extracts coordinates, calculates acreage, and emits outputs.
   */
  private onPolygonChanged(): void {
    if (!this.currentPolygon) {
      return;
    }

    const coords = this.extractPolygonCoordinates(this.currentPolygon);
    if (coords.length < 3) {
      return;
    }

    // Update coordinate display
    this.polygonCoordinates = coords;

    // Calculate acreage using FlightMapService
    const acreage = this.flightMapService.calculateAreaFromCoordinates(coords);
    this.acreageChanged.emit(parseFloat(acreage.toFixed(2)));

    // Convert to GeoJSON and emit
    const geoJson = coordsToGeoJsonPolygon(coords);
    this.boundaryChanged.emit(geoJson);

    // Check vertex count
    this.checkVertexCount(geoJson);
  }

  /**
   * Extract {lat, lng} coordinates from a Google Maps Polygon
   */
  private extractPolygonCoordinates(polygon: google.maps.Polygon): Coordinate[] {
    const path = polygon.getPath();
    const coords: Coordinate[] = [];

    for (let i = 0; i < path.getLength(); i++) {
      const point = path.getAt(i);
      coords.push({ lat: point.lat(), lng: point.lng() });
    }

    return coords;
  }

  /**
   * Check vertex count and show warning if >= 1000
   */
  private checkVertexCount(geoJson: any): void {
    if (isHighVertexCount(geoJson)) {
      const count = getVertexCount(geoJson);
      this.vertexWarning = `This boundary has ${count} vertices. Consider simplifying for better performance.`;
    } else {
      this.vertexWarning = null;
    }
  }

  /**
   * Load existing boundaryGeojson as an editable polygon on the map
   */
  private loadExistingBoundary(): void {
    if (!this.boundaryGeojson || !this.getMap()) {
      return;
    }

    // Defensive: parse if stored as string
    let geojson: any;
    if (typeof this.boundaryGeojson === 'string') {
      try {
        geojson = JSON.parse(this.boundaryGeojson);
      } catch {
        console.error('Invalid boundary GeoJSON string');
        return;
      }
    } else {
      geojson = this.boundaryGeojson;
    }

    const coords = geoJsonPolygonToCoords(geojson);
    if (coords.length < 3) {
      return;
    }

    this.renderPolygonFromCoords(coords);
  }

  /**
   * Render a polygon on the map from coordinates and set it as editable
   */
  private renderPolygonFromCoords(coords: Coordinate[]): void {
    const map = this.getMap();
    if (!map) {
      return;
    }

    // Remove existing polygon
    this.cleanupPolygon();

    const path = coords.map(c => ({ lat: c.lat, lng: c.lng }));

    this.currentPolygon = new google.maps.Polygon({
      paths: path,
      fillColor: '#FF3B30',
      fillOpacity: 0.2,
      strokeColor: '#FF3B30',
      strokeOpacity: 0.9,
      strokeWeight: 2,
      editable: !this.isMobile,
      draggable: false,
      map: map
    });

    this.hasPolygon = true;

    // Switch drawing manager to null mode
    if (this.drawingManager) {
      this.drawingManager.setDrawingMode(null);
    }

    // Attach listeners for editing (desktop only)
    if (!this.isMobile) {
      this.attachPolygonListeners(this.currentPolygon);
    }

    // Calculate and emit
    this.onPolygonChanged();

    // Fit bounds to polygon
    this.fitBoundsToPolygon(coords);
  }

  /**
   * Fit map bounds to show the entire polygon
   */
  private fitBoundsToPolygon(coords: Coordinate[]): void {
    const map = this.getMap();
    if (!map || coords.length === 0) {
      return;
    }

    const bounds = new google.maps.LatLngBounds();
    coords.forEach(c => bounds.extend({ lat: c.lat, lng: c.lng }));
    map.fitBounds(bounds, { top: 50, right: 50, bottom: 50, left: 50 });
  }

  /**
   * Clear the current boundary polygon
   */
  clearBoundary(): void {
    this.cleanupPolygon();
    this.vertexWarning = null;
    this.boundaryChanged.emit(null);
    this.originalFileChanged.emit(null);

    // Re-enable polygon drawing mode
    if (this.drawingManager) {
      this.drawingManager.setDrawingMode(google.maps.drawing.OverlayType.POLYGON);
    }
  }

  // ============================================
  // FILE IMPORT (KML/GeoJSON)
  // ============================================

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.handleFileImport(files[0]);
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.handleFileImport(input.files[0]);
      // Reset input so the same file can be selected again
      input.value = '';
    }
  }

  /**
   * Process an imported boundary file
   */
  private handleFileImport(file: File): void {
    const validTypes = ['.kml', '.geojson', '.json'];
    const extension = '.' + file.name.split('.').pop()?.toLowerCase();

    if (!validTypes.includes(extension)) {
      this.uploadError = 'Please upload a KML or GeoJSON file.';
      return;
    }

    // Confirm replacement if polygon already exists
    if (this.hasPolygon) {
      const confirmed = window.confirm('A boundary already exists. Replace it with the imported file?');
      if (!confirmed) {
        return;
      }
    }

    this.isProcessing = true;
    this.uploadError = null;

    if (extension === '.geojson' || extension === '.json') {
      this.processGeoJsonFile(file);
    } else {
      this.processKmlFile(file);
    }
  }

  /**
   * Process a GeoJSON file import
   */
  private processGeoJsonFile(file: File): void {
    this.flightMapService.parseGeoJsonFile(file).subscribe({
      next: (coords) => {
        this.isProcessing = false;
        this.renderPolygonFromCoords(coords);
        // No original file content for GeoJSON (GeoJSON is the primary format)
        this.originalFileChanged.emit(null);
      },
      error: (err) => {
        this.isProcessing = false;
        this.uploadError = 'Failed to parse GeoJSON file. Please check the file format.';
        console.error('GeoJSON import error:', err);
      }
    });
  }

  /**
   * Process a KML file import.
   * First tries to extract a <Polygon> element; falls back to convex hull from coordinates.
   */
  private processKmlFile(file: File): void {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const xmlString = e.target?.result as string;
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(xmlString, 'text/xml');

        // Check for parsing errors
        const parserError = xmlDoc.querySelector('parsererror');
        if (parserError) {
          throw new Error('Invalid XML/KML file');
        }

        // Try extracting polygon coordinates first
        let coords = extractPolygonFromKml(xmlDoc);

        // Fallback: extract all coordinates and compute convex hull
        if (!coords || coords.length < 3) {
          const allCoords = extractCoordinatesFromKml(xmlDoc);
          if (allCoords.length < 3) {
            throw new Error('No usable coordinates found in file');
          }
          coords = computeConvexHull(allCoords);
        }

        this.ngZone.run(() => {
          this.isProcessing = false;
          this.renderPolygonFromCoords(coords!);
          // Emit original file content for boundary_kml storage
          this.originalFileChanged.emit(xmlString);
        });
      } catch (error: any) {
        this.ngZone.run(() => {
          this.isProcessing = false;
          this.uploadError = 'Failed to process file. Please check the file format.';
          console.error('KML import error:', error);
        });
      }
    };

    reader.onerror = () => {
      this.isProcessing = false;
      this.uploadError = 'Failed to read file.';
    };

    reader.readAsText(file);
  }

  // ============================================
  // IMPORT FROM FLIGHT PATH
  // ============================================

  /**
   * Load flight paths for the field from the API
   */
  private async loadFlightPaths(): Promise<void> {
    if (!this.fieldId) {
      return;
    }

    this.loadingFlightPaths = true;

    try {
      this.flightPaths = await this.fieldService.getFlightPaths(this.fieldId);
    } catch (error) {
      console.error('Failed to load flight paths:', error);
      this.flightPaths = [];
    } finally {
      this.loadingFlightPaths = false;
    }
  }

  /**
   * Handle flight path selection from dropdown
   */
  onFlightPathSelected(): void {
    if (!this.selectedFlightPathId) {
      return;
    }

    const selected = this.flightPaths.find(fp => fp.id === this.selectedFlightPathId);
    if (!selected || !selected.coordinates || selected.coordinates.length < 3) {
      this.uploadError = 'Selected flight path has insufficient coordinates.';
      return;
    }

    // Confirm replacement if polygon already exists
    if (this.hasPolygon) {
      const confirmed = window.confirm('A boundary already exists. Replace it with the flight path boundary?');
      if (!confirmed) {
        this.selectedFlightPathId = null;
        return;
      }
    }

    this.uploadError = null;

    // Compute convex hull from flight path coordinates
    const hull = computeConvexHull(selected.coordinates);

    // Apply buffer distance (~7.5 meters, midpoint of 5-10m range)
    const buffered = applyBuffer(hull, 7.5);

    this.renderPolygonFromCoords(buffered);
    this.originalFileChanged.emit(null);

    // Reset dropdown
    this.selectedFlightPathId = null;
  }
}
